import { useAuthStore, decodeJwtExp, type AuthSession } from './token-store';
import { parseRolesParam } from './roles';
import { http } from '@/api/http';

const API = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');
const ENTRA_TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID; // was hardcoded in the wasm app — now env

// Body of POST /api/auth/exchange — the token bundle the backend stashed under a one-time code.
type ExchangeResponse = {
  token?: string;
  refreshToken?: string;
  email?: string;
  name?: string;
  roles?: string;
};

function scrubUrl(): void {
  // history.replaceState — strip tokens from the URL/back-button (doc 03 §2.2).
  window.history.replaceState(null, document.title, window.location.pathname);
}

function syncAdmin(): void {
  // POST /api/auth/sync — fire-and-forget (mirrors main.kt); non-fatal at login.
  void http.post('api/auth/sync').catch(() => undefined);
}

export const authService = {
  /** Full-page redirect to backend OAuth2 (LoginPage button). */
  login(): void {
    window.location.href = `${API}/oauth2/authorization/azure`;
  },

  /**
   * Run once on app start (AuthProvider). Handles the OAuth2 return:
   * new flow — `?code=…` is exchanged (POST /api/auth/exchange) for the token bundle so tokens never
   * touch the URL (avoids the WAF query-size 403 + token-in-URL leakage); legacy `?token=…` is still
   * accepted as a fallback. Scrubs the URL and persists the session.
   */
  async handleCallback(): Promise<{ handled: boolean; error?: string }> {
    const q = new URLSearchParams(window.location.search);
    const error = q.get('error');
    if (error) {
      scrubUrl();
      return { handled: true, error };
    }

    const persist = (b: {
      token: string;
      refreshToken?: string | null | undefined;
      email?: string | null | undefined;
      name?: string | null | undefined;
      roles?: string | null | undefined;
    }) => {
      const session: AuthSession = {
        accessToken: b.token,
        refreshToken: b.refreshToken ?? '',
        accessTokenExpiresAt: decodeJwtExp(b.token), // real exp from the JWT
        email: b.email ?? '',
        name: b.name ?? '',
        roles: parseRolesParam(b.roles ?? null), // display names -> AppRole[]
      };
      useAuthStore.getState().setSession(session);
      scrubUrl();
      syncAdmin();
    };

    // New flow: one-time login code -> exchange for the token bundle (tokens stay out of the URL).
    const code = q.get('code');
    if (code) {
      try {
        const res = await http.post<ExchangeResponse>('api/auth/exchange', { code });
        const b = res.data;
        if (!b?.token) {
          scrubUrl();
          return { handled: true, error: 'exchange_failed' };
        }
        persist({ token: b.token, refreshToken: b.refreshToken, email: b.email, name: b.name, roles: b.roles });
        return { handled: true };
      } catch {
        scrubUrl();
        return { handled: true, error: 'exchange_failed' };
      }
    }

    // Legacy fallback: tokens passed directly in the URL query.
    const token = q.get('token');
    if (!token) return { handled: false }; // normal navigation, not a callback
    persist({
      token,
      refreshToken: q.get('refreshToken'),
      email: q.get('email'),
      name: q.get('name'),
      roles: q.get('roles'),
    });
    return { handled: true };
  },

  /** Clear local session + Entra logout redirect. */
  logout(): void {
    useAuthStore.getState().clear();
    scrubUrl();
    const redirect = encodeURIComponent(window.location.origin);
    window.location.href = `https://login.microsoftonline.com/${ENTRA_TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${redirect}`;
  },
};
